import {
  type Language,
  type Marketplace,
  type MarketplaceAmazon,
  marketplaceToConfig,
  type PLATFORMS,
  type ShareableFieldValue,
} from '@amzn/journeys-common';
import { uniq } from 'es-toolkit/compat';
import { DestinationUrlInput } from 'features/campaign/campaign.types';
import type { ShareableFieldConfigBase } from 'features/traffic/campaigns/campaign-configs/types';
import { useActiveFlavors, useFlavorFromOtherName } from 'features/traffic/campaigns/useFlavor';
import { useSchema } from 'features/traffic/campaigns/useSchema';
import { generateFlavorCreativeName } from 'features/traffic/campaigns/utils/generate-flavor-creative-name';
import { useSpyOnField } from 'hooks/form-spy/useSpyOnField';
import { useSpyOnFieldValue } from 'hooks/form-spy/useSpyOnFieldValue';
import { useMemo } from 'react';
import { useField } from 'react-final-form';
import { useGetFieldPath } from 'utils/pathing';
import { useValidatePrimitiveSync } from 'utils/validation';
import { linkTypeAndLinkTypeValueSchema } from '../../../validation/generic.validation-schemas';
import type { LinkTypeAndLinkTypeValue, RefTaggedLinkSharing } from '../../generic.types';
import { flavorHasMultipleImageMapAreas } from '../../herotator/herotator-utils/flavor-has-multiple-imagemap-areas';
import type { HerotatorFlavor } from '../../herotator/types';
import { useShareOptions } from '../../shared-variables/hooks/useShareOptions';
import type { FullFormFriendlyPath } from '../../shared-variables/shared-variables.types';
import { useSharedVarsContext } from '../../shared-variables/shared-variables-context';
import { generateDestinationUrlForFlavor } from './generate-destination-url-for-flavor';
import { generateRefTagForFlavor } from './generate-ref-tag-for-flavor';
import { extractLinkTypeAndValue, isLinkTypeAndValueString } from './ref-tagged-link-utils';

type UseRefTaggedLinkEditorProps<TFieldValue extends ShareableFieldValue, TFlavorContent extends Record<string, unknown>> = {
  name: string;
  areaIndex?: number;
  shareableFieldConfig: ShareableFieldConfigBase<TFieldValue, TFlavorContent>;
  removeUrlRefTag?: boolean;
  refTagAutoGenSuffix?: string;
  customRefTagGenerator?: () => string;
};

export const useRefTaggedLinkEditor = <TFieldValue extends ShareableFieldValue, TFlavorContent extends Record<string, unknown>>({
  name,
  areaIndex,
  shareableFieldConfig,
  removeUrlRefTag = false,
  refTagAutoGenSuffix,
  customRefTagGenerator,
}: UseRefTaggedLinkEditorProps<TFieldValue, TFlavorContent>) => {
  const gpLink = useGetFieldPath<RefTaggedLinkSharing>(name);
  const { sharedVarsMap, inlineEditorMeta } = useSharedVarsContext();
  const validateLinkTypeAndValue = useValidatePrimitiveSync(linkTypeAndLinkTypeValueSchema);

  const campaignName: string = useSpyOnFieldValue({
    fieldPath: 'name',
  }) as string;
  const flavor: HerotatorFlavor = useFlavorFromOtherName(name);
  const campaignSchema = useSchema();

  const destinationUrlSpy = useSpyOnField({
    fieldPath: name,
    subscriptionKeys: [
      'value',
      'error',
    ],
  });

  const refTaggedLink = useMemo(
    () => destinationUrlSpy.value as RefTaggedLinkSharing,
    [
      destinationUrlSpy.value,
    ],
  );
  const autoGenerateRefTagField = useField<boolean>(gpLink('autoGenerateRefTag'));

  const destinationUrlInvalid = useMemo(
    () => !!destinationUrlSpy.error,
    [
      destinationUrlSpy.error,
    ],
  );

  const shareOptions = useShareOptions({
    name: name as FullFormFriendlyPath,
    shareableFieldConfig,
  });

  // Check if a Deals Link Type is currently being shared across flavors
  const isDealsLinkSharingEnabled = useMemo(() => {
    if (inlineEditorMeta.editInitiatorPath !== gpLink('linkTypeAndLinkTypeValue')) {
      return false;
    }
    if (!isLinkTypeAndValueString(inlineEditorMeta.value)) return false;
    const { linkType } = extractLinkTypeAndValue(inlineEditorMeta.value as LinkTypeAndLinkTypeValue);
    for (const shareOption of shareOptions) {
      if (linkType === DestinationUrlInput.DEALS && shareOption.isExistingSharedVar === true) {
        return true;
      }
    }
    return false;
  }, [
    gpLink,
    inlineEditorMeta.editInitiatorPath,
    inlineEditorMeta.value,
    shareOptions,
  ]);
  const activeFlavors = useActiveFlavors();
  const flavorMarketplaces = useMemo(
    () => uniq(activeFlavors.map(({ scheduling }) => scheduling.marketplace) ?? []),
    [
      activeFlavors,
    ],
  );

  // The Deals warning popup only appears when there are multiple
  // active marketplaces and when the values are linked
  const renderDealsWarningModal = useMemo(
    () => flavorMarketplaces.length > 1 && isDealsLinkSharingEnabled,
    [
      flavorMarketplaces.length,
      isDealsLinkSharingEnabled,
    ],
  );

  const creativeName = useMemo(() => {
    if (flavor.hasGeneratedName) {
      return generateFlavorCreativeName({
        campaignName,
        campaignSchema,
        language: flavor.scheduling.language as Language,
        marketplace: flavor.scheduling.marketplace as Marketplace,
        platform: flavor.scheduling.platform as PLATFORMS,
        variant: flavor.variant,
      });
    }
    return flavor.creativeName || '';
  }, [
    flavor.hasGeneratedName,
    flavor.creativeName,
    flavor.scheduling.language,
    flavor.scheduling.marketplace,
    flavor.scheduling.platform,
    flavor.variant,
    campaignName,
    campaignSchema,
  ]);

  const hasMultipleAreas = useMemo(
    () => flavorHasMultipleImageMapAreas(flavor),
    [
      flavor,
    ],
  );
  const generatedRefTag = useMemo(
    () =>
      generateRefTagForFlavor({
        areaIndexSuffix: hasMultipleAreas ? areaIndex : undefined,
        creativeName,
        customRefTagGenerator,
      }),
    [
      areaIndex,
      creativeName,
      hasMultipleAreas,
      customRefTagGenerator,
    ],
  );

  const generatedDestinationUrl = useMemo(
    () =>
      generateDestinationUrlForFlavor({
        areaIndex,
        creativeName,
        customRefTagGenerator,
        flavorPlatform: flavor.scheduling.platform as PLATFORMS,
        hasMultipleAreas,
        refTagAutoGenSuffix,
        refTaggedLink,
        removeUrlRefTag,
        sharedVarsMap,
      }),
    [
      refTaggedLink,
      creativeName,
      customRefTagGenerator,
      flavor.scheduling.platform,
      sharedVarsMap,
      hasMultipleAreas,
      areaIndex,
      removeUrlRefTag,
      refTagAutoGenSuffix,
    ],
  );

  const destinationUrlId = `${name}-${areaIndex}-destination-url`;
  const destinationUrlLink = useMemo(() => {
    const currentMarketplace = `${flavor.scheduling.marketplace}Amazon` as MarketplaceAmazon;
    const marketplaceDomain = `${marketplaceToConfig[currentMarketplace].siteName}`;
    return `https://${marketplaceDomain}${generatedDestinationUrl}`;
  }, [
    flavor.scheduling.marketplace,
    generatedDestinationUrl,
  ]);

  return {
    autoGenerateRefTagField,
    destinationUrlId,
    destinationUrlInvalid,
    destinationUrlLink,
    generatedDestinationUrl,
    generatedRefTag,
    gpLink,
    refTaggedLink,
    renderDealsWarningModal,
    validateLinkTypeAndValue,
  };
};
